import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Section.css";
import ImageOverlay from "./components/ImageOverlay";
import React from "react";
import { MOBILE_MAX_WIDTH_PX } from "../../util/Constant";
/* eslint-disable complexity */

gsap.registerPlugin(ScrollTrigger);

const getDynamicSize = (): number => {
  const width = window.innerWidth;
  const minFactor = width > 2600 ? 0.19 : 0.3;

  return (
    width *
    Math.max(
      0.12,
      Math.min(0.55, minFactor + 0.25 * Math.max(0, (1500 - width) / 1200))
    )
  );
};

export interface OverlayItem {
  imageSrc: string;
}

interface SectionProps {
  overlays: OverlayItem[];
}

const Section: React.FC<SectionProps> = ({ overlays }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<number>(getDynamicSize());
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_MAX_WIDTH_PX
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_MAX_WIDTH_PX);

      setSize(getDynamicSize());
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const elements = containerRef.current.querySelectorAll<HTMLDivElement>(
      ".image-lottie-container"
    );

    elements.forEach((el, index) => {
      const yOffset = index === 1 ? (isMobile ? -80 : -80) : 0;
      const yOffsetTo = index === 1 ? (isMobile ? -80 : -50) : 0;

      gsap.fromTo(
        el,
        { y: yOffset },
        {
          y: yOffsetTo,
          scrollTrigger: {
            trigger: containerRef.current!,
            start: "top center",
            end: "bottom center",
            scrub: 1,
          },
        }
      );
    });
  }, []);

  useLayoutEffect(() => {
    // if (isMobile) return;

    if (!containerRef.current) return;

    const elements = containerRef.current.querySelectorAll<HTMLDivElement>(
      ".image-lottie-container"
    );

    elements.forEach((el, index) => {
      let finalX = "0%";
      let initialX = "0%";
      let initialScale = isMobile ? 1.4 : 1;
      let zIndex = 2;

      let initialOffset = width < 1000 ? 95 : 85;
      let finalOffset = width < 1000 ? 80 : 79;

      if (index === 0) {
        initialX = isMobile ? "70%" : `${initialOffset}%`;
        finalX = isMobile ? "70%" : `${finalOffset}%`;
        initialScale = isMobile ? 1.4 : width >= 1700 ? 0.95 : 1;
        zIndex = 1;
      } else if (index === 2) {
        initialX = isMobile ? "-70%" : `-${initialOffset}%`;
        finalX = isMobile ? "-70%" : `-${finalOffset}%`;
        initialScale = isMobile ? 1.4 : width >= 1700 ? 0.95 : 1;
        zIndex = 1;
      }

      el.style.zIndex = zIndex.toString();

      if (isMobile) {
        el.style.transform = "scale(1.4)";
        el.style.transition = "transform 0.3s ease-out";
      }

      gsap.fromTo(
        el,
        { x: initialX, scale: initialScale, opacity: 1 },
        {
          x: finalX,
          scale: isMobile ? 1.4 : width >= 1700 ? 1 : 1.2,
          opacity: 1,
          duration: 3.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: el,
            scrub: true,
          },
        }
      );
    });
  }, [width, size]);

  return (
    <div className="section-three" ref={containerRef}>
      <div className="overlay-row-three">
        {overlays.map((overlay, index) => {
          const isMiddleItem = index === Math.floor(overlays.length / 2);
          return (
            <ImageOverlay
              key={index}
              imageSrc={overlay.imageSrc}
              size={size}
              isMiddleItem={isMiddleItem}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Section;
